import { getApolloClient } from '../lib/getApolloClient';
import { Site, SiteInput, SiteStatus, User } from '../generated/gql-types';
import {
  CREATE_SITE,
  GET_SITE,
  GET_SITES_FOR_CLIENT,
  SET_ACCOUNTS_FOR_SITE,
  SET_SITE_STATUS,
  SET_SITES_FOR_ACCOUNT,
  UPDATE_SITE,
} from './site.gql';

const getSitesForClient = async (
  clientId: string,
  status?: SiteStatus[],
): Promise<Site[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_SITES_FOR_CLIENT,
      variables: { clientId, status },
      fetchPolicy: 'network-only',
    });
    return data?.getSitesForClient ?? [];
  } catch (error) {
    console.error('Error fetching sites for client:', error);
    throw error;
  }
};

const getSiteById = async (id: string): Promise<Site | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_SITE,
      variables: { id },
    });
    return data?.getSite ?? null;
  } catch (error) {
    console.error('Error fetching site by id:', error);
    return null;
  }
};

const createSite = async (
  details: SiteInput,
): Promise<{ success: boolean; site?: Site; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: CREATE_SITE,
      variables: { details },
    });

    if (!data?.createSite) {
      return { success: false, error: 'Failed to create site' };
    }

    return { success: true, site: data.createSite };
  } catch (error) {
    console.error('Error creating site:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const updateSite = async (
  id: string,
  details: SiteInput,
): Promise<{ success: boolean; site?: Site; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: UPDATE_SITE,
      variables: { id, details },
    });

    if (!data?.updateSite) {
      return { success: false, error: 'Failed to update site' };
    }

    return { success: true, site: data.updateSite };
  } catch (error) {
    console.error('Error updating site:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const setSiteStatus = async (
  id: string,
  status: SiteStatus,
): Promise<{ success: boolean; site?: Site; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: SET_SITE_STATUS,
      variables: { id, status },
    });

    if (!data?.setSiteStatus) {
      return { success: false, error: 'Failed to set site status' };
    }

    return { success: true, site: data.setSiteStatus };
  } catch (error) {
    console.error('Error setting site status:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const setAccountsForSite = async (
  siteId: string,
  clientUserIds?: string[],
  inviteIds?: string[],
): Promise<{ success: boolean; users?: User[]; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: SET_ACCOUNTS_FOR_SITE,
      variables: { siteId, clientUserIds, inviteIds },
    });

    if (!data?.setAccountsForSite) {
      return { success: false, error: 'Failed to update site accounts' };
    }

    return { success: true, users: data.setAccountsForSite };
  } catch (error) {
    console.error('Error updating site accounts:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const setSitesForAccount = async (params: {
  clientUserId?: string;
  inviteId?: string;
  siteIds: string[];
}): Promise<{ success: boolean; siteIds?: string[]; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: SET_SITES_FOR_ACCOUNT,
      variables: {
        clientUserId: params.clientUserId,
        inviteId: params.inviteId,
        siteIds: params.siteIds,
      },
    });

    if (!data?.setSitesForAccount) {
      return { success: false, error: 'Failed to update site access' };
    }

    return { success: true, siteIds: data.setSitesForAccount };
  } catch (error) {
    console.error('Error updating site access:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

export const siteActions = {
  getSitesForClient,
  getSiteById,
  createSite,
  updateSite,
  setSiteStatus,
  setAccountsForSite,
  setSitesForAccount,
};
